/** @jsx jsx */
// External libraries
import React, { Fragment, useEffect, useState } from 'react';
import { jsx } from '@emotion/core';
import { useStoreActions, useStoreState } from '@/store';
import throttle from 'lodash.throttle';
import {
  Button,
  Interstitial,
  PayPalLogoMonotone,
  // @ts-expect-error untyped-import
} from '@checkout/react-components';

// Internal modules
import {
  features,
  getContent,
  getToken,
  postToMerchantForOldIncontext,
} from '@/lib';
import { calculateCookieBannerHeight } from './utils';
import { getInterstitialStyles, interstitialOverlayStyles } from './styles';
import { VenmoHeader } from './VenmoHeader';
import { VenmoOverlay } from './VenmoOverlay';
import { VENMO_OVERLAY_VARIANTS } from './constants';
import {
  logAppNotFound,
  logCancelAndRTM,
  logProcessPayWithPayPal,
} from './fpti';

type VenmoMessageProps = {
  buttonSessionId: string;
};

const VenmoMessage = (props: VenmoMessageProps): JSX.Element => {
  const isFormSubmitting = useStoreState(({ ui }) => ui.isFormSubmitting);
  const cancelUrl = useStoreState(({ api }) => api.cancelUrl);
  // @ts-expect-error Fix intent state
  const intent = useStoreState(({ api }) => api.intent);
  const setShowVenmoMessage = useStoreActions(
    ({ ui }) => ui.setShowVenmoMessage,
  );
  const [cookieBannerOffset, setCookieBannerOffset] = useState(
    calculateCookieBannerHeight(),
  );

  useEffect(() => {
    const handleWindowResize = throttle(() => {
      setCookieBannerOffset(calculateCookieBannerHeight());
    }, 200);
    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  logAppNotFound(props.buttonSessionId);

  const handleRTM = (e: React.MouseEvent) => {
    e.preventDefault();

    if (isFormSubmitting) {
      return;
    }

    logCancelAndRTM();

    if (window.xprops) {
      const token = getToken();
      const data = {
        cancelUrl,
        intent: intent?.toLowerCase(),
        orderID: token,
        paymentToken: token,
      };

      window.xprops.onCancel?.(data);
      return;
    }

    // for old checkout.js integration send post message
    const didPostToMerchantForOldIncontext =
      postToMerchantForOldIncontext(cancelUrl);
    if (didPostToMerchantForOldIncontext) {
      return;
    }

    window.location.assign(cancelUrl);
  };

  const handleContinue = () => {
    logProcessPayWithPayPal(props.buttonSessionId);
    setShowVenmoMessage(false);
  };

  const renderVenmoOverlayVariant = (variant: string) => {
    switch (variant) {
      case VENMO_OVERLAY_VARIANTS.INTERSTITIAL_WITH_RTM:
        return (
          <VenmoOverlay onCancel={handleRTM} onContinue={handleContinue} />
        );
      case VENMO_OVERLAY_VARIANTS.INTERSTITIAL_WITH_LOGIN:
        return <VenmoOverlay hasLogin={true} onContinue={handleContinue} />;
      case VENMO_OVERLAY_VARIANTS.CONTROL:
      default:
        return (
          <Fragment>
            <h3>
              {getContent('valueProps.payWith.guest.venmo.noApp')}
              <br />
              {getContent('valueProps.payWith.guest.venmo.continueWithPaypal')}
            </h3>

            <Button
              aria-label={getContent('buttons.guest.payWithPaypal')}
              onClick={handleContinue}
            >
              <span>{getContent('buttons.guest.payWith')}</span>
              <PayPalLogoMonotone />
            </Button>
          </Fragment>
        );
    }
  };

  if (features.hasFeature('venmoOverlay', VENMO_OVERLAY_VARIANTS.HEADER)) {
    return <VenmoHeader />;
  }

  return (
    <Interstitial
      closeOnOverlayClick={true}
      interstitialStyleOverrides={getInterstitialStyles(cookieBannerOffset)}
      onClose={() => setShowVenmoMessage(false)}
      overlayStyleOverrides={interstitialOverlayStyles}
      withoutCloseButton={true}
      withoutPayPalLogo={true}
    >
      {renderVenmoOverlayVariant(features.config.venmoOverlay)}
    </Interstitial>
  );
};

export { VenmoMessage };
